import type { RoleRef } from '@dawn/feature-config';
import { Button, DisabledWithReason, Spinner, cn } from '@dawn/ui';
import { Ban, Pencil, UserCheck } from 'lucide-react';
import { useState } from 'react';

import { useActivateAdmin } from '@/hooks/mutations/use-activate-admin';
import { useSuspendAdmin } from '@/hooks/mutations/use-suspend-admin';
import { usePermissions } from '@/hooks/use-permissions';
import { getActionErrorMessage } from '@/lib/admin-action-error';
import type { AdminListItem } from '@/lib/api';
import { useSessionStore } from '@/stores/session';

import { AdminRoleDialog } from './AdminRoleDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { RoleChips } from './RoleChips';

/** Why a management action is disabled for a viewer without `users.manage`. */
export const MANAGE_REASON = 'Requires Super Admin';
const SELF_SUSPEND_REASON = 'You cannot suspend yourself';
const SELF_EDIT_REASON = 'You cannot change your own roles';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface AdminsTableProps {
  admins: AdminListItem[];
  /** The tenant's roles, the Edit dialog's options. */
  availableRoles: RoleRef[];
}

/**
 * Admins table — people with admin access, active or suspended. The Roles column
 * lists each admin's roles (TDEV-1213). Every admin carries a Suspend/Activate
 * toggle matching the backend lifecycle: suspending cuts access behind a
 * destructive confirm but keeps the seat; activating restores it directly. A
 * Status column distinguishes Active from Suspended rows (TDEV-140). Without
 * `users.manage` the toggles are disabled with the reason, and your own row never
 * offers Suspend; the server refuses the last active Super Admin (TDEV-1213).
 */
export function AdminsTable({ admins, availableRoles }: AdminsTableProps) {
  const { can } = usePermissions();
  const userId = useSessionStore((s) => s.user?.id);
  const manageReason = can('users.manage') ? null : MANAGE_REASON;
  const [suspendTarget, setSuspendTarget] = useState<AdminListItem | null>(null);
  const [editTarget, setEditTarget] = useState<AdminListItem | null>(null);
  const suspend = useSuspendAdmin();
  const activate = useActivateAdmin();
  const suspending = suspend.isPending;
  const activating = activate.isPending;
  // Serialize the table's controls while any one action is in flight.
  const busy = suspending || activating;

  const handleConfirmSuspend = async () => {
    if (!suspendTarget) return;
    try {
      await suspend.mutateAsync(suspendTarget.id);
      setSuspendTarget(null);
    } catch {
      // Error surfaces inside the dialog; keep it open so the user can retry.
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      setSuspendTarget(null);
      suspend.reset();
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Email
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Roles
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Status
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Date added
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Last login
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => {
              const suspended = admin.suspendedAt != null;
              const activatingRow = activating && activate.variables === admin.id;
              const activateFailedRow = activate.isError && activate.variables === admin.id;
              const suspendReason = admin.id === userId ? SELF_SUSPEND_REASON : manageReason;
              const editReason = admin.id === userId ? SELF_EDIT_REASON : manageReason;
              return (
                <tr
                  key={admin.id}
                  className="border-b last:border-0"
                  data-testid={`admin-row-${admin.id}`}
                >
                  <td className="px-4 py-3 text-sm text-dawn-black">{admin.email}</td>
                  <td className="px-4 py-3 text-sm">
                    <RoleChips roles={admin.roles} testId={`admin-roles-${admin.id}`} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                        suspended ? 'bg-error/10 text-error' : 'bg-green-100 text-green-700'
                      )}
                      data-testid={`admin-status-${admin.id}`}
                    >
                      {suspended ? 'Suspended' : 'Active'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDate(admin.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDate(admin.lastLogin)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <DisabledWithReason reason={editReason} placement="top-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5"
                          aria-label={`Edit roles of ${admin.email}`}
                          onClick={() => setEditTarget(admin)}
                          disabled={busy}
                          data-testid={`admin-edit-button-${admin.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                      </DisabledWithReason>
                      {suspended ? (
                        <>
                          {activateFailedRow && (
                            <span
                              className="max-w-[16rem] text-right text-xs text-error"
                              data-testid={`admin-activate-error-${admin.id}`}
                            >
                              {getActionErrorMessage(
                                activate.error,
                                "Couldn't activate. Please try again."
                              )}
                            </span>
                          )}
                          {/* A suspended Admin has no session, so Activate never meets your own row. */}
                          <DisabledWithReason reason={manageReason} placement="top-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="gap-1.5"
                              aria-label={`Activate ${admin.email}`}
                              onClick={() => activate.mutate(admin.id)}
                              disabled={busy}
                              data-testid={`admin-activate-button-${admin.id}`}
                            >
                              {activatingRow ? (
                                <Spinner animate size={14} />
                              ) : (
                                <UserCheck className="h-3.5 w-3.5" />
                              )}
                              Activate
                            </Button>
                          </DisabledWithReason>
                        </>
                      ) : (
                        <DisabledWithReason reason={suspendReason} placement="top-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5 text-error hover:text-error"
                            aria-label={`Suspend ${admin.email}`}
                            onClick={() => setSuspendTarget(admin)}
                            disabled={busy}
                            data-testid={`admin-suspend-button-${admin.id}`}
                          >
                            <Ban className="h-3.5 w-3.5" />
                            Suspend
                          </Button>
                        </DisabledWithReason>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={suspendTarget !== null}
        onOpenChange={handleDialogOpenChange}
        title="Suspend user"
        description={
          <>
            Suspend <span className="font-medium text-dawn-black">{suspendTarget?.email}</span>?
            They&apos;ll immediately lose access to this dashboard; you can reactivate them later.
          </>
        }
        confirmLabel="Suspend"
        onConfirm={() => void handleConfirmSuspend()}
        loading={suspending}
        error={
          suspend.isError
            ? getActionErrorMessage(suspend.error, "Couldn't suspend the user. Please try again.")
            : null
        }
        data-testid="suspend-admin-dialog"
      />

      {/* Mounted per target and keyed on it, so every open starts from that row's roles. */}
      {editTarget && (
        <AdminRoleDialog
          key={editTarget.id}
          mode="edit-admin"
          admin={editTarget}
          open
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
          availableRoles={availableRoles}
        />
      )}
    </>
  );
}
